import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent, ReactElement } from 'react';

type SearchableSelectProps<TOption> = {
  id?: string;
  options: ReadonlyArray<TOption>;
  /** The selected option, or null when nothing is selected. */
  value: TOption | null;
  onChange: (option: TOption) => void;
  getOptionKey: (option: TOption) => string;
  getOptionLabel: (option: TOption) => string;
  placeholder?: string;
  noResultsText?: string;
  containerClassName?: string;
};

/**
 * A dropdown whose input box does the searching. Opening it shows every option (the selected one
 * highlighted); typing in the box narrows the list.
 */
export function SearchableSelect<TOption>({
  id,
  options,
  value,
  onChange,
  getOptionKey,
  getOptionLabel,
  placeholder = 'Select...',
  noResultsText = 'No matches.',
  containerClassName = 'relative w-[400px] min-w-[200px] max-w-full',
}: SearchableSelectProps<TOption>): ReactElement {
  const generatedId = useId();
  const baseId = id ?? `searchable-select-${generatedId}`;
  const listboxId = `${baseId}-listbox`;
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeOptionRef = useRef<HTMLLIElement>(null);

  const selectedKey = value === null ? null : getOptionKey(value);
  const selectedLabel = value === null ? '' : getOptionLabel(value);

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter((option) => getOptionLabel(option).toLowerCase().includes(term));
  }, [options, query, getOptionLabel]);

  function open(): void {
    const selectedIndex = options.findIndex((option) => getOptionKey(option) === selectedKey);
    setQuery('');
    setActiveIndex(Math.max(0, selectedIndex));
    setIsOpen(true);
  }

  function close(returnFocus: boolean): void {
    setIsOpen(false);
    if (returnFocus) inputRef.current?.focus();
  }

  function select(option: TOption): void {
    onChange(option);
    close(true);
  }

  useEffect(() => {
    activeOptionRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function handlePointerDown(event: MouseEvent): void {
      if (!containerRef.current?.contains(event.target as Node)) setIsOpen(false);
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isOpen]);

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (!isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        open();
      }
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((index) => Math.min(index + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const option = filtered[activeIndex];
      if (option !== undefined) select(option);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
    } else if (event.key === 'Tab') {
      setIsOpen(false);
    }
  }

  return (
    <div ref={containerRef} className={containerClassName}>
      <div className="relative">
        <input
          ref={inputRef}
          id={baseId}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          autoComplete="off"
          value={isOpen ? query : selectedLabel}
          placeholder={isOpen && selectedLabel ? selectedLabel : placeholder}
          onChange={(event) => {
            if (!isOpen) setIsOpen(true);
            setQuery(event.target.value);
            setActiveIndex(0);
          }}
          onFocus={() => {
            if (!isOpen) open();
          }}
          onClick={() => {
            if (!isOpen) open();
          }}
          onKeyDown={handleKeyDown}
          className={`w-full rounded-lg border border-slate-300 bg-white py-2 pl-3 pr-9 text-sm text-slate-900 shadow-sm outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-200 ${
            isOpen && selectedLabel ? 'placeholder:text-slate-900' : 'placeholder:text-slate-400'
          }`}
        />
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </div>
      {isOpen && (
        <div className="absolute z-20 mt-1 w-full rounded-lg border border-slate-200 bg-white shadow-lg">
          <ul id={listboxId} role="listbox" className="max-h-72 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm text-slate-500">{noResultsText}</li>
            ) : (
              filtered.map((option, index) => {
                const key = getOptionKey(option);
                const isSelected = key === selectedKey;
                const isActive = index === activeIndex;
                return (
                  <li
                    key={key}
                    ref={isActive ? activeOptionRef : undefined}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => select(option)}
                    className={`flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm ${
                      isActive ? 'bg-sky-100 text-sky-900' : 'text-slate-700'
                    } ${isSelected ? 'font-semibold' : ''}`}
                  >
                    <span>{getOptionLabel(option)}</span>
                    {isSelected && (
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4"
                        aria-hidden="true"
                      >
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    )}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
